// GlimtCamera — fullskjerm mobil kamera med tap/hold, front/bak-toggle, galleri-picker.
// Bruker getUserMedia + MediaRecorder. Etter opptak sendes bilde/video til onCapture.
import React, { useEffect, useRef, useState } from "react";
import { X, RefreshCw, Image as ImageIcon, Zap, ZapOff } from "lucide-react";

export default function GlimtCamera({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const holdTimerRef = useRef(null);
  const holdStartRef = useRef(0);
  const progressRef = useRef(null);
  const [facing, setFacing] = useState("environment"); // environment | user
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing }, width: { ideal: 1080 }, height: { ideal: 1920 } },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(e?.message || "Kamera-tilgang ble avslått");
      }
    }
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
    };
  }, [facing]);

  const snapPhoto = () => {
    const v = videoRef.current;
    if (!v) return;
    const w = v.videoWidth || 720;
    const h = v.videoHeight || 1280;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Speil bare preview for front-kamera, ikke lagret media (Snap-standard)
    ctx.drawImage(v, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob) onCapture({ type: "image", blob, width: w, height: h });
    }, "image/jpeg", 0.92);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported("video/mp4;codecs=avc1")
      ? "video/mp4;codecs=avc1"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    recorderRef.current = rec;
    rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime });
      const v = videoRef.current;
      onCapture({
        type: "video",
        blob,
        width: v?.videoWidth || 720,
        height: v?.videoHeight || 1280,
        mimeType: mime,
      });
    };
    rec.start();
    setRecording(true);
    holdStartRef.current = performance.now();
    const tick = () => {
      const elapsed = (performance.now() - holdStartRef.current) / 1000;
      const p = Math.min(100, (elapsed / 60) * 100);
      setProgress(p);
      if (elapsed >= 60) {
        stopRecording();
      } else if (recorderRef.current?.state === "recording") {
        progressRef.current = requestAnimationFrame(tick);
      }
    };
    progressRef.current = requestAnimationFrame(tick);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
    if (progressRef.current) cancelAnimationFrame(progressRef.current);
    setProgress(0);
  };

  // tap = bilde, hold = video
  const onCaptureDown = (e) => {
    e.preventDefault();
    holdTimerRef.current = setTimeout(() => {
      startRecording();
    }, 350);
  };
  const onCaptureUp = (e) => {
    e.preventDefault();
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (recording) {
      stopRecording();
    } else {
      snapPhoto();
    }
  };

  const pickFromGallery = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isVid = (f.type || "").startsWith("video/");
    onCapture({ type: isVid ? "video" : "image", blob: f, mimeType: f.type });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="glimt-camera">
      <video
        ref={videoRef}
        playsInline
        muted
        className="w-full h-full object-cover"
        style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
      />
      {/* Topp-rad */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white"
          data-testid="glimt-cam-close"
          aria-label="Lukk"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <button
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white opacity-40"
            aria-label="Blits (kommer)"
            disabled
          >
            <ZapOff className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress-ring for video-opptak */}
      {recording && (
        <div className="absolute inset-x-0 top-4 flex justify-center z-10">
          <div className="px-3 py-1 rounded-full bg-red-600/80 text-white text-xs font-bold tracking-wide">
            REC · {Math.floor(progress * 60 / 100)}s
          </div>
        </div>
      )}
      {!ready && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-[var(--cream-soft)] text-sm">
          Starter kamera…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
          <div className="text-red-400 text-sm">Feil: {error}</div>
        </div>
      )}

      {/* Bunn-kontroller */}
      <div className="absolute bottom-0 left-0 right-0 pb-8 pt-6 flex items-center justify-around z-10">
        {/* Galleri-thumbnail */}
        <label className="w-12 h-12 rounded-lg bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center cursor-pointer text-white" data-testid="glimt-cam-gallery">
          <ImageIcon className="w-5 h-5" />
          <input type="file" accept="image/*,video/*" className="hidden" onChange={pickFromGallery} />
        </label>

        {/* Snap-knapp */}
        <button
          onPointerDown={onCaptureDown}
          onPointerUp={onCaptureUp}
          onPointerLeave={(e) => { if (recording || holdTimerRef.current) onCaptureUp(e); }}
          className={`w-20 h-20 rounded-full border-4 ${recording ? "border-red-500" : "border-white"} flex items-center justify-center`}
          data-testid="glimt-cam-snap"
          aria-label="Ta bilde eller hold for video"
        >
          <div className={`rounded-full ${recording ? "bg-red-500 w-8 h-8" : "bg-white w-16 h-16"}`} style={{ transition: "all 0.15s" }} />
        </button>

        {/* Front/bak-toggle */}
        <button
          onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
          className="w-12 h-12 rounded-lg bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center text-white"
          data-testid="glimt-cam-flip"
          aria-label="Bytt kamera"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="absolute bottom-32 left-0 right-0 text-center text-white/60 text-[11px] pointer-events-none">
        Tap = bilde · Hold = video (60s)
      </div>
    </div>
  );
}
